import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ArtifactPanel } from '@/spaces/chat/panels/ArtifactPanel';
import { EditScheduleSubPanel } from '@/spaces/chat/panels/EditScheduleSubPanel';
import { InsightPanel } from '@/spaces/chat/panels/InsightPanel';
import { MonitorActionPanel } from '@/spaces/chat/panels/MonitorActionPanel';
import { ownerActionFromPanel } from '@/spaces/chat/panels/ownerActionFromPanel.ts';
import { OwnerActionPanel } from '@/spaces/chat/panels/OwnerActionPanel.tsx';
import { shouldShowBackChevron } from '@/spaces/chat/panels/panelLabel';
import { ProcessPanel } from '@/spaces/chat/panels/ProcessPanel';
import { ProcessRecipientsPanel } from '@/spaces/chat/panels/ProcessRecipientsPanel';
import { ProcessSchedulePanel } from '@/spaces/chat/panels/ProcessSchedulePanel';
import { StrategyReviewPanel } from '@/spaces/chat/panels/StrategyReviewPanel';

import type {
	ChatPanel as ChatPanelState,
	ProcessChatPanel,
} from '../../../../../bridge/types/state.ts';

interface ChatPanelProps {
	panel: ChatPanelState;
	style?: React.CSSProperties;
}

export const ChatPanel = ({ panel, style }: ChatPanelProps) => {
	const bridgeApi = useBridgeApi();
	const strategiesEnabled = useFeatureFlag('strategies');

	const handlePanelClose = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_close_chat_panel' });
	}, [bridgeApi]);

	const handlePanelBack = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
	}, [bridgeApi]);

	const history = bridgeApi.user_state?.chat_panel_history ?? [];
	const hasHistory = shouldShowBackChevron(history);
	const onBack = hasHistory ? handlePanelBack : undefined;

	if (panel.panel_type === 'artifact') {
		return (
			<ArtifactPanel
				panel={panel}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	if (panel.panel_type === 'process') {
		return (
			<ProcessPanel
				panel={panel}
				onClose={handlePanelClose}
				hasHistory={hasHistory}
				style={style}
			/>
		);
	}

	if (panel.panel_type === 'process_recipients') {
		return (
			<ProcessRecipientsPanel
				processId={panel.item_id}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	if (panel.panel_type === 'process_schedule') {
		const liveProcess = [...history]
			.reverse()
			.find(
				(p): p is ProcessChatPanel =>
					p.panel_type === 'process' && p.item_id === panel.item_id,
			)?.process;

		if (
			liveProcess &&
			(liveProcess.status === 'active' || liveProcess.status === 'paused')
		) {
			return (
				<EditScheduleSubPanel
					panelProcess={liveProcess}
					onBack={onBack}
					onClose={handlePanelClose}
					style={style}
				/>
			);
		}

		return (
			<ProcessSchedulePanel
				processId={panel.item_id}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	if (panel.panel_type === 'action') {
		// An owner ask is an action that carries real content of its own, so it
		// gets a real panel rather than the monitor's config stepper.
		if (panel.action_type === 'request_owner_action') {
			const ownerAction = ownerActionFromPanel(bridgeApi.user_state, panel);
			if (ownerAction) {
				return (
					// Keyed by ask: the panel holds "this step is confirmed" in local
					// state. Without a key React reuses one instance for the next ask,
					// so the owner opens unread work already showing the success screen.
					<OwnerActionPanel
						key={ownerAction.id}
						action={ownerAction}
						onBack={onBack}
						onClose={handlePanelClose}
						style={style}
					/>
				);
			}
		}
		return (
			<MonitorActionPanel
				panel={panel}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	if (panel.panel_type === 'curated_insight') {
		return (
			<InsightPanel
				panel={panel}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	// Mounting this panel is what asks the server to build the campaigns, so the gate has
	// to sit above the render, not inside it.
	if (panel.panel_type === 'strategy' && strategiesEnabled) {
		return (
			<StrategyReviewPanel
				// Remount on a strategy change. `useStrategyDetail` deliberately keeps the previous
				// run while the next one loads, so without this the panel can hold strategy A's
				// plan under strategy B's id for a frame — long enough for the guided walk to
				// correct B's cursor from A's step list.
				//
				// And on a THREAD change, which the plan id alone cannot see. Two threads can hold
				// the same strategy: `LINKED_ITEM_PANELS` maps `strategy_flag` to a strategy panel
				// keyed by `metadata.strategy_id`, so a flag thread about this plan builds the same
				// `item_id`. `Chat.tsx` renders one unkeyed `ChatPanel` and `restorePanelsDeltas`
				// writes `chat_panel` with a single `value_set`, so nothing else remounts it. The
				// instance's own state then followed the owner across: a failed save's red
				// "This plan is being changed right now." rendered in the second thread, over a
				// plan they were only reading and had attempted no save on.
				key={`${panel.chat_id}:${panel.item_id}`}
				panel={panel}
				onBack={onBack}
				onClose={handlePanelClose}
				style={style}
			/>
		);
	}

	return null;
};
